import { ALLOWED_IMAGE_TYPES, ALLOWED_VIDEO_TYPES, PlaylistMediaType } from '@/models/media-network/Playlist';

export interface MediaMetadata {
    mediaType: PlaylistMediaType;
    width?: number;
    height?: number;
    /** Whole seconds, videos only. Advisory — the device advances on its player's own end-of-media signal. */
    durationSeconds?: number;
}

/**
 * Reads dimensions (and duration, for video) off the file the user just picked.
 *
 * The browser already holds the bytes, so this is free: an <img> exposes naturalWidth/naturalHeight and a
 * <video> exposes videoWidth/videoHeight/duration once it fires 'loadedmetadata'. The alternative — parsing
 * the MP4 moov/mvhd atom server-side — needs a range read back out of blob storage and the atom is not
 * reliably at the head of a non-faststart file, which is a lot of fragility for a number sitting right here.
 *
 * Never rejects: metadata is advisory, and a browser that can't decode a file it will nonetheless upload
 * fine shouldn't block the upload. Callers get an empty result and the backend stores nulls.
 */
export async function readMediaMetadata(file: File): Promise<MediaMetadata> {
    if (ALLOWED_IMAGE_TYPES.includes(file.type)) {
        return readImageMetadata(file);
    }
    if (ALLOWED_VIDEO_TYPES.includes(file.type)) {
        return readVideoMetadata(file);
    }
    return { mediaType: PlaylistMediaType.Image };
}

function readImageMetadata(file: File): Promise<MediaMetadata> {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();

        const done = (meta: MediaMetadata) => {
            URL.revokeObjectURL(url);
            resolve(meta);
        };

        img.onload = () => done({
            mediaType: PlaylistMediaType.Image,
            width: img.naturalWidth || undefined,
            height: img.naturalHeight || undefined,
        });
        img.onerror = () => done({ mediaType: PlaylistMediaType.Image });
        img.src = url;
    });
}

function readVideoMetadata(file: File): Promise<MediaMetadata> {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement('video');
        video.preload = 'metadata';

        const done = (meta: MediaMetadata) => {
            URL.revokeObjectURL(url);
            resolve(meta);
        };

        video.onloadedmetadata = () => {
            // A stream with an unknown length reports Infinity; the backend rejects a non-positive duration,
            // so send nothing rather than something false.
            const duration = Number.isFinite(video.duration) && video.duration > 0
                ? Math.round(video.duration)
                : undefined;

            done({
                mediaType: PlaylistMediaType.Video,
                width: video.videoWidth || undefined,
                height: video.videoHeight || undefined,
                durationSeconds: duration,
            });
        };
        video.onerror = () => done({ mediaType: PlaylistMediaType.Video });
        video.src = url;
    });
}

// There is deliberately no content-hash helper here any more.
//
// The upload used to compute a SHA-256 client-side, which meant reading the whole file into memory — a 250 MB
// ArrayBuffer at the ceiling — purely to produce a value the server had no way to verify. The backend now
// derives the hash at commit from what storage recorded (the blob's Content-MD5, falling back to its ETag),
// which it can actually trust and which costs the browser nothing. Don't reintroduce one.
